import * as THREE from 'three';
import { distToSegment2 } from '../../shared/geom.js';
import { BuildingIndex, SurfaceIndex } from '../../shared/collision.js';
import { Heightfield } from '../../shared/heightfield.js';
import { ROAD_SPECS } from '../../shared/osm/classify.js';
import type { Area, Road, World } from '../../shared/types.js';
import type { SignBoard } from './storefronts.js';
import type { StreetLamp } from './streetLight.js';
import { buildCanopies, buildStores } from './landmarks.js';
import { buildCarParks } from './carParks.js';
import { buildPitches } from './pitches.js';
import { buildTerraces } from './terraces.js';

/**
 * Everything in the city that is neither a building nor a tree: filling stations, the
 * entrances and names of supermarkets and shopping centres, car-park bays, sports pitches
 * and the tables outside cafés. All of it modelled here, in code, from the OSM data the
 * world carries — see DECISIONS.md §96.
 *
 * **One draw call.** It is all vertex-coloured, flat-shaded and static, so it all goes into
 * one merged mesh, the way the buildings do: a city has thousands of chairs, and instancing
 * each kind of thing would be a draw call per kind for the same triangles. Two attributes
 * stand in for what would otherwise be separate materials — `gloss`, which makes a pane of
 * glass smooth enough to reflect the sky, and `glow`, which lights a canopy's underside, a
 * supermarket's glass and a pump's screen after dark. The names go in `storefronts.ts`'s
 * atlas (`boards`), and the forecourt lights in the street-light map (`lights`).
 *
 * **Nothing here is solid.** A car drives under a canopy, through a pump and over a café
 * table: they are scenery, the car's physics never sees them, and the AI and the race are
 * exactly what they were. The rule that keeps that honest is that nothing is put where a
 * car is meant to be — every placement is tested against the carriageway first.
 */

export interface CityObjectMeshes {
  /** Empty until `furnish`. */
  objects: THREE.Object3D[];
  triangles: number;
  /** Names to paint, for `buildStorefronts`. */
  boards: SignBoard[];
  /** Where the lights under the canopies are, for `buildStreetLight`. */
  lights: StreetLamp[];
  /**
   * Puts the café tables out, spending at most what it is given, and builds the mesh.
   *
   * A second step because the tables are what a city can have any number of, and the budget
   * they get is what is left once the shop signs are built — which need the names above
   * first. Called once.
   */
  furnish(budget: number): void;
  setNight(amount: number): void;
  dispose(): void;
}

/** What every part of the build shares: the indices, the terrain and where the route is. */
export interface ObjectContext {
  world: World;
  terrain: Heightfield;
  surface: SurfaceIndex;
  buildings: BuildingIndex;
  edges: CarriagewayEdges;
  /** The same, for the footways and pedestrian streets the car may not drive on. */
  walkways: CarriagewayEdges;
  /** Pedestrian squares, which a café fronts as surely as it fronts a street. */
  squares: Area[];
  /** Squared distance to the race route, for spending a budget nearest it first. */
  routeDistance2(x: number, z: number): number;
  boards: SignBoard[];
  lights: StreetLamp[];
}

/**
 * Café furniture is the one thing here a city can have a great deal of, so it is capped, and
 * spent nearest the route first. It is built after everything else, so this is never what
 * leaves the budget short.
 */
const TERRACE_MAX_TRIANGLES = 30_000;
/**
 * And never more than half of what the city has left, so the chimneys after it are not left
 * with nothing: in the Marais, which builds to within a few thousand triangles of the budget,
 * the tables took all of it the first time and Paris lost its chimney pots.
 */
const TERRACE_SHARE = 0.5;

export function buildCityObjects(world: World, terrain: Heightfield): CityObjectMeshes {
  const builder = new ObjectBuilder();
  const route = world.route.path;
  const ctx: ObjectContext = {
    world,
    terrain,
    surface: new SurfaceIndex(world),
    buildings: new BuildingIndex(world),
    edges: new CarriagewayEdges(world.roads),
    walkways: new CarriagewayEdges(world.roads, false),
    squares: world.areas.filter((a) => a.kind === 'paved'),
    routeDistance2(x, z) {
      let best = Infinity;
      for (let i = 0; i + 3 < route.length; i += 2) {
        best = Math.min(best, distToSegment2(x, z, route[i], route[i + 1], route[i + 2], route[i + 3]).d2);
      }
      return best;
    },
    boards: [],
    lights: [],
  };

  buildCanopies(builder, ctx);
  buildStores(builder, ctx);
  buildCarParks(builder, ctx);
  buildPitches(builder, ctx);

  const disposables: { dispose(): void }[] = [];
  const night = { value: 0 };
  const result: CityObjectMeshes = {
    objects: [],
    triangles: builder.triangles,
    boards: ctx.boards,
    lights: ctx.lights,
    furnish(budget) {
      buildTerraces(builder, ctx, Math.min(TERRACE_MAX_TRIANGLES, Math.max(0, budget) * TERRACE_SHARE));
      result.triangles = builder.triangles;
      const mesh = builder.toMesh(night);
      if (mesh) {
        result.objects.push(mesh);
        disposables.push(mesh.geometry, mesh.material as THREE.Material, mesh.customDepthMaterial!);
      }
    },
    setNight(amount) {
      night.value = amount;
    },
    dispose() {
      for (const d of disposables) d.dispose();
    },
  };
  return result;
}

// ---------------------------------------------------------------------------
// shared helpers
// ---------------------------------------------------------------------------

/** Zero on a flat world, so everything stands exactly where it did before terrain existed. */
export function lowestGround(poly: number[], terrain: Heightfield): number {
  if (terrain.isFlat) return 0;
  let lowest = Infinity;
  for (let i = 0; i < poly.length; i += 2) lowest = Math.min(lowest, terrain.heightAt(poly[i], poly[i + 1]));
  return Number.isFinite(lowest) ? lowest : 0;
}

/**
 * Distance to the edge of the nearest carriageway — the drawn one, not the slightly narrower
 * strip the physics calls road — through a grid of segments. Negative inside one.
 *
 * `SurfaceIndex` answers "is this tarmac" and is what the car believes; this answers "how
 * far is the kerb", which is what a table needs to keep clear of. Both are asked.
 */
export class CarriagewayEdges {
  private static readonly CELL = 24;
  private readonly cells = new Map<number, number[]>();
  private readonly segs: number[] = [];
  private readonly halfWidths: number[] = [];

  constructor(roads: Road[], drivable = true) {
    const C = CarriagewayEdges.CELL;
    for (const road of roads) {
      if (ROAD_SPECS[road.cls].drivable !== drivable) continue;
      for (let i = 0; i + 3 < road.pts.length; i += 2) {
        const index = this.halfWidths.length;
        const ax = road.pts[i];
        const az = road.pts[i + 1];
        const bx = road.pts[i + 2];
        const bz = road.pts[i + 3];
        this.segs.push(ax, az, bx, bz);
        this.halfWidths.push(road.width / 2);
        for (let cx = Math.floor(Math.min(ax, bx) / C); cx <= Math.floor(Math.max(ax, bx) / C); cx++) {
          for (let cz = Math.floor(Math.min(az, bz) / C); cz <= Math.floor(Math.max(az, bz) / C); cz++) {
            const key = cx * 73856093 + cz;
            const bucket = this.cells.get(key);
            if (bucket) bucket.push(index);
            else this.cells.set(key, [index]);
          }
        }
      }
    }
  }

  /** Metres from the point to the nearest carriageway's edge; Infinity if none is within a cell. */
  distance(x: number, z: number): number {
    const C = CarriagewayEdges.CELL;
    const cx = Math.floor(x / C);
    const cz = Math.floor(z / C);
    let best = Infinity;
    for (let dx = -1; dx <= 1; dx++) {
      for (let dz = -1; dz <= 1; dz++) {
        const bucket = this.cells.get((cx + dx) * 73856093 + cz + dz);
        if (!bucket) continue;
        for (const i of bucket) {
          const { d2 } = distToSegment2(x, z, this.segs[i * 4], this.segs[i * 4 + 1], this.segs[i * 4 + 2], this.segs[i * 4 + 3]);
          best = Math.min(best, Math.sqrt(d2) - this.halfWidths[i]);
        }
      }
    }
    return best;
  }
}

/** Whether a spot is clear of the carriageway by `margin`, by both measures, and of walls. */
export function clearOfTraffic(ctx: ObjectContext, x: number, z: number, margin: number): boolean {
  if (ctx.surface.sample(x, z).onRoad) return false;
  return ctx.edges.distance(x, z) >= margin;
}

/** How high the ground anything stands on is drawn at: the pavement's top, or the bare ground. */
export function standingHeight(ctx: ObjectContext, x: number, z: number): number {
  const s = ctx.surface.sample(x, z);
  // Behind a kerb is pavement, drawn on the kerb's top: 0.16 m over a street at 0.14–0.2.
  const lift = !s.onRoad && s.roughness < 1 ? 0.31 : 0.1;
  return ctx.terrain.heightAt(x, z) + lift;
}

// ---------------------------------------------------------------------------
// ObjectBuilder
// ---------------------------------------------------------------------------

const _c = new THREE.Color();

/**
 * A `gloss` that is not a gloss: chain-link, for a fence or a net. The shader cuts the face
 * into wire diamonds a few centimetres across, filtered so that from further off — where a
 * diamond is smaller than a pixel — the wire's share of each pixel is kept by dithering
 * instead, and the fence is a haze rather than a moiré. It casts no shadow; the posts do.
 */
export const MESH = -1;

/**
 * The fragment code for `MESH`: needs `vObjPos` and `vObjNormal` in world space. One family
 * of wires either diagonal, 6 cm apart, each a sixth of that wide.
 */
const MESH_DISCARD = `
	if ( vGloss < -0.5 ) {
		vec2 across = normalize( vec2( -vObjNormal.z, vObjNormal.x ) + 1e-5 );
		vec2 q = vec2( dot( vObjPos.xz, across ), vObjPos.y ) / 0.06;
		float wireA = q.x + q.y;
		float wireB = q.x - q.y;
		float fa = max( fwidth( wireA ), 1e-4 );
		float fb = max( fwidth( wireB ), 1e-4 );
		float w = 0.16;
		float ca = ( floor( wireA + fa * 0.5 ) * w + min( fract( wireA + fa * 0.5 ), w ) - floor( wireA - fa * 0.5 ) * w - min( fract( wireA - fa * 0.5 ), w ) ) / fa;
		float cb = ( floor( wireB + fb * 0.5 ) * w + min( fract( wireB + fb * 0.5 ), w ) - floor( wireB - fb * 0.5 ) * w - min( fract( wireB - fb * 0.5 ), w ) ) / fb;
		float cover = 1.0 - ( 1.0 - ca ) * ( 1.0 - cb );
		float dither = fract( 52.9829189 * fract( dot( gl_FragCoord.xy, vec2( 0.06711056, 0.00583715 ) ) ) );
		if ( cover < dither ) discard;
	}
`;

/**
 * Accumulates flat-shaded, vertex-coloured triangles, with a per-vertex gloss and glow, into
 * one buffer. Everything is non-indexed so every face keeps its own normal — the same trade
 * `worldMesh.ts`'s builder makes.
 */
/**
 * How far paint on the ground is pulled towards the camera in depth: eight steps of a 24-bit
 * depth buffer, the bias `polygonOffset` would give it if this mesh could have two materials.
 *
 * Bay lines and a forecourt's concrete lie a centimetre or two over the area fill under them,
 * and a fill is lifted wherever the ground sags, so the two meet. Up close a centimetre is
 * plenty; from a few hundred metres it is less than the depth buffer can tell apart, and the
 * paint and the tarmac took turns as the camera moved. The bias grows with distance the way
 * that blindness does, and at the street it is a fraction of a millimetre.
 */
const OBJECT_DECAL_BIAS = (8 * 2) / 2 ** 24;

export class ObjectBuilder {
  private readonly positions: number[] = [];
  private readonly normals: number[] = [];
  private readonly colors: number[] = [];
  private readonly glosses: number[] = [];
  private readonly glows: number[] = [];
  private readonly decals: number[] = [];
  private r = 1;
  private g = 1;
  private b = 1;
  private gloss = 0;
  private glow = 0;
  private decal = 0;

  get triangles(): number {
    return this.positions.length / 9;
  }

  /** What the triangles after this are painted: a colour, how glassy, how lit at night. */
  paint(color: number, gloss = 0, glow = 0): this {
    _c.setHex(color, THREE.SRGBColorSpace);
    this.r = _c.r;
    this.g = _c.g;
    this.b = _c.b;
    this.gloss = gloss;
    this.glow = glow;
    this.decal = 0;
    return this;
  }

  /**
   * Marks what follows, until the next `paint`, as paint on the ground: bay lines and a
   * forecourt's concrete. See `OBJECT_DECAL_BIAS`.
   */
  onGround(): this {
    this.decal = 1;
    return this;
  }

  /** One triangle, its normal from its winding. */
  tri(
    ax: number, ay: number, az: number,
    bx: number, by: number, bz: number,
    cx: number, cy: number, cz: number,
  ): void {
    const ux = bx - ax, uy = by - ay, uz = bz - az;
    const vx = cx - ax, vy = cy - ay, vz = cz - az;
    let nx = uy * vz - uz * vy;
    let ny = uz * vx - ux * vz;
    let nz = ux * vy - uy * vx;
    const len = Math.hypot(nx, ny, nz);
    if (len < 1e-12) return;
    nx /= len;
    ny /= len;
    nz /= len;
    this.positions.push(ax, ay, az, bx, by, bz, cx, cy, cz);
    for (let i = 0; i < 3; i++) {
      this.normals.push(nx, ny, nz);
      this.colors.push(this.r, this.g, this.b);
      this.glosses.push(this.gloss);
      this.glows.push(this.glow);
      this.decals.push(this.decal);
    }
  }

  /**
   * A quad from four corners in order round its edge, turned to face `facing`. Callers say
   * which way a face looks rather than keeping track of winding, which in a frame that can be
   * mirrored is where every one of these bugs comes from.
   */
  quad(p: number[], facing: [number, number, number]): void {
    const ux = p[3] - p[0], uy = p[4] - p[1], uz = p[5] - p[2];
    const vx = p[6] - p[0], vy = p[7] - p[1], vz = p[8] - p[2];
    const nx = uy * vz - uz * vy;
    const ny = uz * vx - ux * vz;
    const nz = ux * vy - uy * vx;
    if (nx * facing[0] + ny * facing[1] + nz * facing[2] >= 0) {
      this.tri(p[0], p[1], p[2], p[3], p[4], p[5], p[6], p[7], p[8]);
      this.tri(p[0], p[1], p[2], p[6], p[7], p[8], p[9], p[10], p[11]);
    } else {
      this.tri(p[0], p[1], p[2], p[6], p[7], p[8], p[3], p[4], p[5]);
      this.tri(p[0], p[1], p[2], p[9], p[10], p[11], p[6], p[7], p[8]);
    }
  }

  /** Both faces of a thin sheet: a net, a sign seen from behind. */
  sheet(p: number[], facing: [number, number, number]): void {
    this.quad(p, facing);
    this.quad(p, [-facing[0], -facing[1], -facing[2]]);
  }

  /**
   * A box in a frame on the ground: `a` along the frame's `u`, `b` along its `v`, `h` up.
   * The bottom is left off unless asked for: almost everything stands on something.
   */
  box(f: Frame, a0: number, a1: number, b0: number, b1: number, h0: number, h1: number, bottom = false): void {
    const p = (a: number, b: number, h: number) => f.at(a, b, h);
    const c = [
      p(a0, b0, h0), p(a1, b0, h0), p(a1, b1, h0), p(a0, b1, h0),
      p(a0, b0, h1), p(a1, b0, h1), p(a1, b1, h1), p(a0, b1, h1),
    ];
    const face = (i: number[], n: [number, number, number]) =>
      this.quad([...c[i[0]], ...c[i[1]], ...c[i[2]], ...c[i[3]]], n);
    face([0, 1, 5, 4], [-f.vx, 0, -f.vz]);
    face([1, 2, 6, 5], [f.ux, 0, f.uz]);
    face([2, 3, 7, 6], [f.vx, 0, f.vz]);
    face([3, 0, 4, 7], [-f.ux, 0, -f.uz]);
    face([4, 5, 6, 7], [0, 1, 0]);
    if (bottom) face([0, 1, 2, 3], [0, -1, 0]);
  }

  /**
   * A square-section bar between two points: a post, a rail, a goal's crossbar, the stay of
   * a net. Four sides, no ends — a bar is never looked at end-on closely enough to miss them.
   */
  beam(
    ax: number, ay: number, az: number,
    bx: number, by: number, bz: number,
    thickness: number,
  ): void {
    const dx = bx - ax, dy = by - ay, dz = bz - az;
    const len = Math.hypot(dx, dy, dz);
    if (len < 1e-4) return;
    const tx = dx / len, ty = dy / len, tz = dz / len;
    // Any vector not along the bar, crossed with it, gives the two sides.
    let sx = 0, sy = 1, sz = 0;
    if (Math.abs(ty) > 0.9) {
      sx = 1;
      sy = 0;
    }
    let px = ty * sz - tz * sy, py = tz * sx - tx * sz, pz = tx * sy - ty * sx;
    const pl = Math.hypot(px, py, pz);
    px /= pl;
    py /= pl;
    pz /= pl;
    const qx = ty * pz - tz * py, qy = tz * px - tx * pz, qz = tx * py - ty * px;
    const h = thickness / 2;
    const corner = (s: number, t: number, end: 0 | 1) => {
      const ox = (px * s + qx * t) * h;
      const oy = (py * s + qy * t) * h;
      const oz = (pz * s + qz * t) * h;
      return end === 0 ? [ax + ox, ay + oy, az + oz] : [bx + ox, by + oy, bz + oz];
    };
    const sides: [number, number, number, number][] = [
      [1, 1, 1, -1],
      [1, -1, -1, -1],
      [-1, -1, -1, 1],
      [-1, 1, 1, 1],
    ];
    for (const [s0, t0, s1, t1] of sides) {
      const n: [number, number, number] = [
        px * (s0 + s1) + qx * (t0 + t1),
        py * (s0 + s1) + qy * (t0 + t1),
        pz * (s0 + s1) + qz * (t0 + t1),
      ];
      this.quad([...corner(s0, t0, 0), ...corner(s1, t1, 0), ...corner(s1, t1, 1), ...corner(s0, t0, 1)], n);
    }
  }

  /** A strip of paint lying on the ground, `width` wide, from one point to another. */
  stripe(ax: number, ay: number, az: number, bx: number, by: number, bz: number, width: number): void {
    const dx = bx - ax;
    const dz = bz - az;
    const len = Math.hypot(dx, dz);
    if (len < 1e-4) return;
    const ox = (-dz / len) * (width / 2);
    const oz = (dx / len) * (width / 2);
    this.quad([ax + ox, ay, az + oz, bx + ox, by, bz + oz, bx - ox, by, bz - oz, ax - ox, ay, az - oz], [0, 1, 0]);
  }

  /** A flat polygon at one height, facing up or down. */
  polygon(poly: number[], y: number, up: boolean): void {
    const ring: THREE.Vector2[] = [];
    for (let i = 0; i < poly.length; i += 2) ring.push(new THREE.Vector2(poly[i], poly[i + 1]));
    const faces = THREE.ShapeUtils.triangulateShape(ring, []);
    for (const [a, b, c] of faces) {
      const pa = ring[a], pb = ring[b], pc = ring[c];
      // Winding is decided by the normal check rather than trusted from the triangulator.
      const cross = (pb.x - pa.x) * (pc.y - pa.y) - (pb.y - pa.y) * (pc.x - pa.x);
      // In (x, z) a positive cross is clockwise seen from above, which faces down.
      const flip = cross > 0 === up;
      if (flip) this.tri(pa.x, y, pa.y, pc.x, y, pc.y, pb.x, y, pb.y);
      else this.tri(pa.x, y, pa.y, pb.x, y, pb.y, pc.x, y, pc.y);
    }
  }

  toMesh(night: { value: number }): THREE.Mesh | null {
    if (!this.positions.length) return null;
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(this.positions, 3));
    geometry.setAttribute('normal', new THREE.Float32BufferAttribute(this.normals, 3));
    geometry.setAttribute('color', new THREE.Float32BufferAttribute(this.colors, 3));
    geometry.setAttribute('gloss', new THREE.Float32BufferAttribute(this.glosses, 1));
    geometry.setAttribute('glow', new THREE.Float32BufferAttribute(this.glows, 1));
    geometry.setAttribute('decal', new THREE.Float32BufferAttribute(this.decals, 1));
    geometry.computeBoundingSphere();
    // Seen from the wrong side, a net or a fence is drawn by its other face: `sheet` emits both.
    const material = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.78, metalness: 0 });
    const vertexDecl =
      'attribute float gloss;\nattribute float glow;\nattribute float decal;\nvarying float vGloss;\nvarying float vGlow;\nvarying vec3 vObjPos;\nvarying vec3 vObjNormal;';
    const vertexBody =
      '\tvGloss = gloss;\n\tvGlow = glow;\n\tvObjPos = ( modelMatrix * vec4( position, 1.0 ) ).xyz;\n\tvObjNormal = normal;';
    const fragmentDecl = 'varying float vGloss;\nvarying float vGlow;\nvarying vec3 vObjPos;\nvarying vec3 vObjNormal;';
    material.onBeforeCompile = (shader) => {
      shader.uniforms.objectNight = night;
      shader.vertexShader = shader.vertexShader
        .replace('#include <common>', `#include <common>\n${vertexDecl}`)
        .replace('#include <begin_vertex>', `#include <begin_vertex>\n${vertexBody}`)
        .replace(
          '#include <project_vertex>',
          `#include <project_vertex>\n\tgl_Position.z -= decal * ${OBJECT_DECAL_BIAS.toExponential(3)} * gl_Position.w;`,
        );
      shader.fragmentShader = shader.fragmentShader
        .replace('#include <common>', `#include <common>\nuniform float objectNight;\n${fragmentDecl}`)
        .replace('#include <clipping_planes_fragment>', `#include <clipping_planes_fragment>\n${MESH_DISCARD}`)
        // Glass and polished paint: smooth enough for the sky to show in it (§80).
        .replace(
          '#include <roughnessmap_fragment>',
          '#include <roughnessmap_fragment>\n\troughnessFactor = mix( roughnessFactor, 0.06, max( vGloss, 0.0 ) );',
        )
        // Fluorescent white, as a forecourt and a supermarket are lit: whiter than the lamps.
        .replace(
          '#include <emissivemap_fragment>',
          '#include <emissivemap_fragment>\n\ttotalEmissiveRadiance += vec3( 1.0, 0.93, 0.82 ) * vGlow * objectNight;',
        );
    };
    material.customProgramCacheKey = () => 'cityObjects';
    const mesh = new THREE.Mesh(geometry, material);
    // The shadow pass draws with a depth material of its own, which would cast a fence's mesh
    // as a solid wall of shadow. This one leaves it out.
    const depth = new THREE.MeshDepthMaterial({ depthPacking: THREE.RGBADepthPacking });
    depth.onBeforeCompile = (shader) => {
      shader.vertexShader = shader.vertexShader
        .replace('#include <common>', '#include <common>\nattribute float gloss;\nvarying float vGloss;')
        .replace('#include <begin_vertex>', '#include <begin_vertex>\n\tvGloss = gloss;');
      shader.fragmentShader = shader.fragmentShader
        .replace('#include <common>', '#include <common>\nvarying float vGloss;')
        .replace('#include <clipping_planes_fragment>', '#include <clipping_planes_fragment>\n\tif ( vGloss < -0.5 ) discard;');
    };
    depth.customProgramCacheKey = () => 'cityObjectsDepth';
    mesh.customDepthMaterial = depth;
    mesh.name = 'city-objects';
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    mesh.matrixAutoUpdate = false;
    return mesh;
  }
}

/**
 * A local frame on the ground: an origin, a unit `u` and the `v` square to it, and the
 * height everything in it is measured from. A pitch is drawn in its own frame, a pump island
 * in its canopy's, a café table in its wall's.
 */
export class Frame {
  readonly vx: number;
  readonly vz: number;
  constructor(
    readonly ox: number,
    readonly oz: number,
    readonly ux: number,
    readonly uz: number,
    readonly y: number,
  ) {
    this.vx = -uz;
    this.vz = ux;
  }

  at(a: number, b: number, h: number): [number, number, number] {
    return [this.ox + this.ux * a + this.vx * b, this.y + h, this.oz + this.uz * a + this.vz * b];
  }

  x(a: number, b: number): number {
    return this.ox + this.ux * a + this.vx * b;
  }

  z(a: number, b: number): number {
    return this.oz + this.uz * a + this.vz * b;
  }
}

/**
 * The smallest rectangle round a ring, taken over the directions of its own edges — which for
 * the rectangles OSM is mostly made of is the rectangle itself. `u` is the long side.
 */
export function orientedBox(poly: number[]): { cx: number; cz: number; ux: number; uz: number; halfLength: number; halfWidth: number } | null {
  const n = poly.length / 2;
  if (n < 3) return null;
  let best: { cx: number; cz: number; ux: number; uz: number; halfLength: number; halfWidth: number; area: number } | null = null;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const dx = poly[j * 2] - poly[i * 2];
    const dz = poly[j * 2 + 1] - poly[i * 2 + 1];
    const len = Math.hypot(dx, dz);
    if (len < 0.5) continue;
    const ux = dx / len;
    const uz = dz / len;
    let a0 = Infinity, a1 = -Infinity, b0 = Infinity, b1 = -Infinity;
    for (let k = 0; k < n; k++) {
      const x = poly[k * 2];
      const z = poly[k * 2 + 1];
      const a = x * ux + z * uz;
      const b = -x * uz + z * ux;
      a0 = Math.min(a0, a);
      a1 = Math.max(a1, a);
      b0 = Math.min(b0, b);
      b1 = Math.max(b1, b);
    }
    const area = (a1 - a0) * (b1 - b0);
    if (best && area >= best.area - 1e-6) continue;
    const ca = (a0 + a1) / 2;
    const cb = (b0 + b1) / 2;
    const long = a1 - a0 >= b1 - b0;
    best = {
      cx: ca * ux - cb * uz,
      cz: ca * uz + cb * ux,
      ux: long ? ux : -uz,
      uz: long ? uz : ux,
      halfLength: (long ? a1 - a0 : b1 - b0) / 2,
      halfWidth: (long ? b1 - b0 : a1 - a0) / 2,
      area,
    };
  }
  return best;
}
